import { whenExtensionDetected, whenElementVisible, templateHtml, escapeHtml, accentedHtml } from './helpers';

// The walk through the tools table, shared by lite and the signed-in tools page (both render
// TableComponents::UndistractorSettingsTabbedComponent): the visitor enables their first tool, sees what it
// does, then watches it switched back off. Toggles are dummy clicks — UI only, so nothing reaches the
// extension, server or analytics, and the first real enable stays the visitor's own (UNS-4789).

const ROW_SELECTOR = '.undistractor-row';
const CHECKBOX_SELECTOR = '.undistractor-checkbox';
// The checkbox is a transparent hit layer spanning the whole switch column, so the spotlight
// follows the label instead — same box as the visible switch. Not .undistractor-card-track:
// the sidebar renders one of those too.
const SWITCH_SELECTOR = '.undistractor-card-switch';
const TOOL_SELECTOR = '.undistractor-card-eyebrow';
const SITE_SELECTOR = '.undistractor-card-name';
const TAB_SELECTOR = '.tab-btn';
const WRAPPER_SELECTOR = '[data-undistractor-tab-filter-undistractor-param]';
// Server-rendered by the page (shared/_undistractor_intro_tour_demo); its copy is written about one
// specific row (see DEMO row below).
const DEMO_TEMPLATE_ID = 'undistractorIntroTourDemo';
// The tab's own "?" explainer modal, already on the page. Cloned rather than re-rendered into a template:
// the slideshows are ~13KB of markup each, and a second copy would drift from the modal's.
const EXPLAINER_MODAL_PREFIX = 'undistractorExplainerModal-';
const EXPLAINER_SHOWCASE_SELECTOR = '.showcase-container';
const EXPLAINER_TITLE_SELECTOR = '.intro-title';
// Every step after the first centres its copy; the ones carrying a slideshow widen for it too. Passed in
// full because driver.js appends a step's popoverClass to the config-level one.
const CENTERED_POPOVER = 'unscreen-tour-popover unscreen-tour-popover--centered';
const SHOWCASE_POPOVER = `${CENTERED_POPOVER} unscreen-tour-popover--demo`;
const TOGGLE_PAUSE_MS = 450;

const visible = (element) => Boolean(element) && element.offsetParent !== null;

// Such a row reads On from its schedule, so no toggle can move the switch — and a real one hands off to the
// schedule controller's master-off, which deletes the window.
const scheduled = (row) => {
  try {
    return Boolean(JSON.parse(row.dataset.undistractorSettingsPageRowUndistractorOptionValue || '{}').schedule);
  } catch {
    return true;
  }
};

export default {
  mount({ start, payload, element }) {
    // Every other step is written around the demo, so a page that can't render one has no tour to give and
    // the visitor gets the page control would have got. The page drops the template when the pinned
    // pairing is missing or its captures aren't built (UndistractorOption#preview?).
    if (!payload.demo_key || !templateHtml(DEMO_TEMPLATE_ID)) return undefined;

    let tour;
    // Pinned once rather than re-queried per step: the tab filter can change which rows are visible, and a
    // tour that switched rows mid-flight would toggle one switch and restore another.
    let row;
    let originalActive = false;
    let demonstrable = false;
    const cleanups = [];

    // The row the demo step is written about. data-undistractor-checkbox-id is the option's
    // browser_extension_key — a pinned cross-client contract, so it survives renames the copy would not.
    const demoRow = () => {
      if (!payload.demo_key) return null;
      return element.querySelector(`${CHECKBOX_SELECTOR}[data-undistractor-checkbox-id="${payload.demo_key}"]`)?.closest(ROW_SELECTOR) ?? null;
    };

    const tabButton = (slug) => element.querySelector(`${TAB_SELECTOR}[data-persistent-tabs-tab-param="${slug}"]`);

    // On a ?tab= URL the demo row is filtered out. Show its tab rather than tour a different row, or a
    // third of visitors would get YouTube copy beside something else.
    const showTabFor = (target) => {
      const slug = target.closest(WRAPPER_SELECTOR)?.dataset.undistractorTabFilterUndistractorParam;
      if (slug) tabButton(slug)?.click();
    };

    // The tour walks every tab, so it puts the visitor back on the one they arrived on.
    let originalTabSlug;
    const restoreTab = () => {
      if (originalTabSlug) tabButton(originalTabSlug)?.click();
    };

    const checkbox = () => row?.querySelector(CHECKBOX_SELECTOR);
    const switchLabel = () => row?.querySelector(SWITCH_SELECTOR);
    const rowSlug = () => row?.closest(WRAPPER_SELECTOR)?.dataset.undistractorTabFilterUndistractorParam;

    // Tracked so a tour destroyed mid-pause can't toggle after onDestroyed turned dummy-click off —
    // that click would write through for real.
    let pauseTimers = [];
    const pause = (callback) => pauseTimers.push(setTimeout(callback, TOGGLE_PAUSE_MS));
    const clearPauses = () => {
      pauseTimers.forEach(clearTimeout);
      pauseTimers = [];
    };
    cleanups.push(clearPauses);

    const advance = () => pause(() => tour?.moveNext());

    const setDummyClick = (on) => {
      if (row) row.dataset.undistractorSettingsPageRowDummyClickValue = String(on);
    };

    // Only safe while dummy-click is on.
    const toggleTo = (active) => {
      const box = checkbox();
      if (box && box.checked !== active) box.click();
    };

    // Undoes the demonstration and gives the row back to the visitor, once. The closing step asks for a real
    // switch, so it has to run before that renders: until dummy-click is off the row swallows clicks UI-only,
    // reaching neither the extension nor the metric the ask exists to move. Idempotent because the teardown
    // calls it too — a second run would undo the very enable the closing step asked for.
    let handedBack = false;
    const handBack = () => {
      // Dummy-click was never armed on a scheduled row, so toggleTo below would land as a real click.
      if (handedBack || !demonstrable) return;

      handedBack = true;
      // A demonstration pause still in flight would flip their switch back under them.
      clearPauses();
      toggleTo(originalActive);
      setDummyClick(false);
    };

    // Read off the row rather than baked into the copy: the eyebrow and name render through
    // UndistractorNaming, which can vary them per visitor.
    const rowCopy = () => ({
      tool: escapeHtml(row.querySelector(TOOL_SELECTOR)?.textContent.trim() || 'this tool'),
      site: escapeHtml(row.querySelector(SITE_SELECTOR)?.textContent.trim() || 'distracting sites'),
    });

    const explainer = (slug) => document.getElementById(`${EXPLAINER_MODAL_PREFIX}${slug}`);

    // Slideshow borrowed from the tab's explainer modal, caption written for the tour. tab_copy is the only
    // thing deciding which tabs are toured: no line for a slug and the tab is skipped.
    // The heading rides inside the description rather than driver.js's own title slot: the popover renders
    // title-then-description in that order, which put the tool's name a whole slideshow away from the line
    // explaining it.
    const explainerContent = (slug) => {
      const copy = (payload.tab_copy || {})[slug];
      const showcase = copy && explainer(slug)?.querySelector(EXPLAINER_SHOWCASE_SELECTOR);
      if (!showcase) return null;

      const heading = explainer(slug)?.querySelector(EXPLAINER_TITLE_SELECTOR)?.textContent.trim();
      return [showcase.outerHTML, heading ? `<h2 class="tour-heading">${escapeHtml(heading)}</h2>` : '', `<p class="tour-text">${accentedHtml(copy)}</p>`].join('');
    };

    // One per tab, in the order the page lists them, minus the one the demo step already covered. The tab is
    // both the thing highlighted and the thing clicked, so the rows behind the popover are the ones the
    // slideshow describes.
    const tabSteps = () => {
      const covered = rowSlug();

      return [...element.querySelectorAll(TAB_SELECTOR)]
        .map((button) => button.dataset.persistentTabsTabParam)
        .filter((slug) => slug && slug !== covered)
        .map((slug) => ({ slug, content: explainerContent(slug) }))
        .filter(({ content }) => content)
        .map(({ slug, content }) => ({
          element: () => tabButton(slug),
          popover: {
            description: content,
            popoverClass: SHOWCASE_POPOVER,
            side: 'bottom',
          },
          onHighlightStarted: () => tabButton(slug)?.click(),
        }));
    };

    const openingStep = ({ tool, site }) => ({
      element: switchLabel,
      popover: demonstrable
        ? {
            title: 'Try your first tool',
            description: `This switch enables <strong>${tool}</strong> on <strong>${site}</strong>. We'll flip it so you can see what it does.`,
            side: 'bottom',
            // Defining onNextClick suppresses driver.js's own advance, so the move happens here, a beat after
            // the toggle so the switch is seen sliding rather than already over — with the card out of the way
            // for that beat. No un-hiding: driver.js sets the wrapper back to display block on every render.
            onNextClick: (_element, _step, { state }) => {
              if (state.popover) state.popover.wrapper.style.display = 'none';
              toggleTo(true);
              advance();
            },
          }
        : {
            title: 'Your first tool',
            description: `You've got <strong>${tool}</strong> running on <strong>${site}</strong> on a schedule. Here's what it does.`,
            side: 'bottom',
          },
    });

    // handBack leaves the switch however the row arrived, so the line has to describe what they can see.
    const closingDescription = (tool) => {
      if (!demonstrable) return `That's the tour! We've left <strong>${tool}</strong> running on its schedule. Flip any other switch to enable a tool for real.`;

      return originalActive
        ? `That's the tour! We've left <strong>${tool}</strong> on, just as you had it. Flip any other switch to enable a tool for real.`
        : `That's the tour! We've switched <strong>${tool}</strong> back off. Now flip any switch to enable a tool for real.`;
    };

    // Asks for the switch rather than flipping it: every toggle up to here has been a dummy click, so the
    // enable this invites is the visitor's first real one, made by them (UNS-4789).
    //
    // No element, so the popover centres and nothing is spotlit. driver.js only lifts pointer-events on the
    // highlighted node, so a spotlight here would make "any switch" mean one switch — and on a ?tab= arrival
    // that one is a row this step's copy isn't about. Centred, they press Done and get the whole table back.
    // Restoring the tab here rather than at teardown keeps the page from moving as the tour disappears.
    const closingStep = (tool) => ({
      popover: {
        title: 'Over to you',
        description: closingDescription(tool),
        popoverClass: CENTERED_POPOVER,
      },
      onHighlightStarted: () => {
        restoreTab();
        handBack();
      },
    });

    const config = () => {
      const { tool, site } = rowCopy();

      const steps = [
        openingStep({ tool, site }),
        {
          popover: {
            description: templateHtml(DEMO_TEMPLATE_ID),
            popoverClass: SHOWCASE_POPOVER,
            nextBtnText: 'Next',
          },
        },
        ...tabSteps(),
        closingStep(tool),
      ];

      return {
        // No-op once the closing step has handed back — past that point any switch on the row is the
        // visitor's own, and restoring over it would undo the enable the tour just asked for.
        onDestroyed: () => {
          restoreTab();
          handBack();
        },
        steps,
      };
    };

    const begin = () => {
      demonstrable = !scheduled(row);
      originalActive = checkbox()?.checked === true;
      if (demonstrable) {
        setDummyClick(true);
        // The steps promise a flip from off, so a row already on has to start there — dummy, and handBack
        // puts it back.
        toggleTo(false);
      }
      tour = start(config());
    };

    cleanups.push(
      whenExtensionDetected(() => {
        cleanups.push(
          whenElementVisible(
            ROW_SELECTOR,
            () => {
              // Before anything below can move it: the tab the visitor is left on is the one they arrived on.
              originalTabSlug = element.querySelector(`${TAB_SELECTOR}.active`)?.dataset.persistentTabsTabParam;

              row = demoRow();
              if (row && !visible(row)) showTabFor(row);

              // A tick, not inline: showTabFor's click has to reach the tab filter before visibility is read.
              const timer = setTimeout(() => {
                // Not even its own tab could show it, so there is no tour to give — same call as mount's.
                if (!visible(row)) {
                  restoreTab();
                  return;
                }

                begin();
              }, 0);
              cleanups.push(() => clearTimeout(timer));
            },
            { within: element },
          ),
        );
      }),
    );

    // Leaves dummy-click on: this runs before onDestroyed, which needs it to restore silently.
    return () => cleanups.forEach((cleanup) => cleanup());
  },
};
